import {
  createInstance,
  enums,
  OptimizelyProvider,
  setLogLevel,
  type ReactSDKClient,
} from '@optimizely/react-sdk';

import { getCookieValue } from '@klv/shared/utils/cookies';
import { windowIsDefined } from '@klv/shared/utils/functions';
import { getPageExperimentInformation } from '@klv/shared/utils/google-tag-manager';

import {
  onDecision,
  setContentsquareExperimentIntegrations,
  setContentsquareExperimentVariables,
} from './utils';

import { type ReactNode } from 'react';

const SDK_KEY = process.env.GATSBY_OPTIMIZELY_SDK_KEY ?? '';

const ID_COOKIE_NAME = '_kvyo_mkt_id';

declare global {
  interface Window {
    _kvyo_mkt_exp_provider?: ReactSDKClient;
  }
}

const createOptimizelyInstance = (sdkKey: string) => {
  if (sdkKey && windowIsDefined()) {
    return createInstance({
      sdkKey,
    });
  }

  return false;
};

setLogLevel(process.env.NODE_ENV === 'production' ? 'error' : 'info');

const optimizely = createOptimizelyInstance(SDK_KEY);

if (optimizely) {
  if (windowIsDefined()) {
    window._kvyo_mkt_exp_provider = optimizely;
  }

  optimizely.notificationCenter.addNotificationListener(
    enums.NOTIFICATION_TYPES.DECISION,
    (decision) => onDecision(decision, optimizely),
  );

  optimizely.onReady().then(() => {
    if (windowIsDefined()) {
      optimizely?.getUserContext()?.setAttribute('queryString', window.location.search);
    }

    const pageExperiment = getPageExperimentInformation();
    
    if (pageExperiment) {
      const flagKey = pageExperiment['optimizely-flagKey'];
      optimizely.decide(flagKey);
    
      setContentsquareExperimentVariables(pageExperiment['optimizely-ruleKey'], pageExperiment['optimizely-variationKey']);
      setContentsquareExperimentIntegrations();
    }

    optimizely.track('client_pageview');
    optimizely.fetchQualifiedSegments();
  });
}

export const isMobileDevice = (userAgent: string) => {
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  return mobileRegex.test(userAgent);
};

export const Optimizely = ({
  children,
}: {
  children: ReactNode;
}) => {
  const id = getCookieValue(ID_COOKIE_NAME);
  const internalTester = getCookieValue('_kvyo_mkt_internal_tester');
  const isCustomer = getCookieValue('hasLoggedInBefore') === 'true';
  const userAgent = windowIsDefined() ? window?.navigator?.userAgent : '';
  const isMobile = isMobileDevice(userAgent);

  if (
    !id
    || !optimizely
  ) {
    return children;
  }

  return (
    <OptimizelyProvider
      optimizely={optimizely}
      user={{
        id: id,
        attributes: {
          hasKlaviyoAccountId: isCustomer,
          internalTester: internalTester === '1',
          $opt_user_agent: userAgent,
          isMobile,
        },
      }}
    >
      {children}
    </OptimizelyProvider>
  );
};
